import { z } from 'zod'

import { CaptureRefSchema } from './capture'
import {
  AssignContactRelationshipTypeSchema,
  ContactRelationshipTypeAssignmentSummarySchema,
  ContactRelationshipTypeSummarySchema,
} from './contact-relationship-type'
import { RecordStateSchema } from './merge'
import { optionalDecimalSchema } from './prisma-zod'

// Contact status enum
export enum ContactStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

// =====================
// Lifecycle stage (Path C — Contact is the universal person record)
// =====================
// Where this person is in the funnel. Orthogonal to Entity.lifecycleStage
// (account-level position). A `contact` Contact at an `active_customer` Entity
// is a valid combination — e.g. a new champion at an existing customer.
export enum ContactLifecycleStageEnum {
  NONE = 'none',
  NEW = 'new',
  QUALIFIED = 'qualified',
  CUSTOMER = 'customer',
  CHURNED = 'churned',
}

export const CONTACT_LIFECYCLE_STAGE_COLORS: Record<
  ContactLifecycleStageEnum,
  string
> = {
  [ContactLifecycleStageEnum.NONE]: '#9a9a9e',
  [ContactLifecycleStageEnum.NEW]: '#f59e0b',
  [ContactLifecycleStageEnum.QUALIFIED]: '#3b82f6',
  [ContactLifecycleStageEnum.CUSTOMER]: '#1ead5b',
  [ContactLifecycleStageEnum.CHURNED]: '#6b7280',
}

export const CONTACT_LIFECYCLE_STAGE_DISPLAY_NAMES: Record<
  ContactLifecycleStageEnum,
  string
> = {
  [ContactLifecycleStageEnum.NONE]: 'None',
  [ContactLifecycleStageEnum.NEW]: 'New',
  [ContactLifecycleStageEnum.QUALIFIED]: 'Qualified',
  [ContactLifecycleStageEnum.CUSTOMER]: 'Customer',
  [ContactLifecycleStageEnum.CHURNED]: 'Churned',
}

// =====================
// ContactRelationship types
// =====================
// Built-in vocabulary. `custom` lets users add freeform labels via the `label`
// field. The service layer validates `type` against this enum but accepts any
// non-empty string when `type='custom'`.
export enum ContactRelationshipTypeEnum {
  MANAGER = 'manager',
  REPORTS_TO = 'reports_to',
  COLLEAGUE = 'colleague',
  SPOUSE = 'spouse',
  PARTNER = 'partner',
  REFERRER = 'referrer',
  REFERRED_BY = 'referred_by',
  ASSISTANT_TO = 'assistant_to',
  ASSISTED_BY = 'assisted_by',
  CUSTOM = 'custom',
}

// Inverse mapping used by the service layer when seeding the reverse edge.
// Symmetric types map to themselves (colleague <-> colleague).
export const CONTACT_RELATIONSHIP_INVERSE: Record<
  ContactRelationshipTypeEnum,
  ContactRelationshipTypeEnum
> = {
  [ContactRelationshipTypeEnum.MANAGER]: ContactRelationshipTypeEnum.REPORTS_TO,
  [ContactRelationshipTypeEnum.REPORTS_TO]: ContactRelationshipTypeEnum.MANAGER,
  [ContactRelationshipTypeEnum.COLLEAGUE]:
    ContactRelationshipTypeEnum.COLLEAGUE,
  [ContactRelationshipTypeEnum.SPOUSE]: ContactRelationshipTypeEnum.SPOUSE,
  [ContactRelationshipTypeEnum.PARTNER]: ContactRelationshipTypeEnum.PARTNER,
  [ContactRelationshipTypeEnum.REFERRER]:
    ContactRelationshipTypeEnum.REFERRED_BY,
  [ContactRelationshipTypeEnum.REFERRED_BY]:
    ContactRelationshipTypeEnum.REFERRER,
  [ContactRelationshipTypeEnum.ASSISTANT_TO]:
    ContactRelationshipTypeEnum.ASSISTED_BY,
  [ContactRelationshipTypeEnum.ASSISTED_BY]:
    ContactRelationshipTypeEnum.ASSISTANT_TO,
  [ContactRelationshipTypeEnum.CUSTOM]: ContactRelationshipTypeEnum.CUSTOM,
}

export const CONTACT_RELATIONSHIP_DISPLAY_NAMES: Record<
  ContactRelationshipTypeEnum,
  string
> = {
  [ContactRelationshipTypeEnum.MANAGER]: 'Manager',
  [ContactRelationshipTypeEnum.REPORTS_TO]: 'Reports to',
  [ContactRelationshipTypeEnum.COLLEAGUE]: 'Colleague',
  [ContactRelationshipTypeEnum.SPOUSE]: 'Spouse',
  [ContactRelationshipTypeEnum.PARTNER]: 'Partner',
  [ContactRelationshipTypeEnum.REFERRER]: 'Referrer',
  [ContactRelationshipTypeEnum.REFERRED_BY]: 'Referred by',
  [ContactRelationshipTypeEnum.ASSISTANT_TO]: 'Assistant to',
  [ContactRelationshipTypeEnum.ASSISTED_BY]: 'Assisted by',
  [ContactRelationshipTypeEnum.CUSTOM]: 'Custom',
}

// Contact status colors - using app design system colors
export const CONTACT_STATUS_COLORS: Record<ContactStatusEnum, string> = {
  [ContactStatusEnum.ACTIVE]: '#1ead5b', // Herbed Frittata (green)
  [ContactStatusEnum.INACTIVE]: '#9a9a9e', // Fog (gray)
}

// Contact status display names
export const CONTACT_STATUS_DISPLAY_NAMES: Record<ContactStatusEnum, string> = {
  [ContactStatusEnum.ACTIVE]: 'Active',
  [ContactStatusEnum.INACTIVE]: 'Inactive',
}

// Helper function to get contact status info
export function getContactStatusInfo(status: ContactStatusEnum) {
  return {
    name: CONTACT_STATUS_DISPLAY_NAMES[status],
    color: CONTACT_STATUS_COLORS[status],
  }
}

// Contact sort field enum
export enum ContactSortFieldEnum {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

export const ContactEntityLinkSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  contactId: z.string().uuid(),
  entityId: z.string().uuid(),
  isPrimary: z.boolean(),
  role: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  entity: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      logoUrl: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

export type ContactEntityLink = z.infer<typeof ContactEntityLinkSchema>

// A company link as embedded on a contact: GET /contacts list rows carry only
// the required fields; the detail read carries the full link.
export const ContactEntityLinkSummarySchema = ContactEntityLinkSchema.partial()
  .required({
    id: true,
    entityId: true,
    isPrimary: true,
  })
  .extend({ entity: ContactEntityLinkSchema.shape.entity })

export type ContactEntityLinkSummary = z.infer<
  typeof ContactEntityLinkSummarySchema
>

// Contact schema
export const ContactSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  imageKey: z.string().nullable().optional(),
  imageUrl: z.string().url().nullable().optional(),

  // Birthday (date only; year treated as display data). Surfaced in personal
  // spaces via the CONTACT_BIRTHDAYS flag; drives the daily reminder cron.
  birthday: z.coerce.date().nullable().optional(),

  // Lifecycle (Path C). Stores the org-specific CRM stage id for
  // CONTACT_LIFECYCLE; pipeline stage lives on Deal, not here.
  lifecycleStage: z.string().optional(),

  // Vendor lane (orthogonal to lifecycleStage). Stores the org-specific CRM
  // stage id for CONTACT_VENDOR_LIFECYCLE (none / potential_vendor / vendor).
  vendorStage: z.string().optional(),

  // Absorbed from Contact (all nullable)
  source: z.string().nullable().optional(),
  score: optionalDecimalSchema.nullable(),
  scoreCalculatedAt: z.date().nullable().optional(),
  assignedToId: z.string().uuid().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  companySize: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  referredBy: z.string().nullable().optional(),
  website: z.string().nullable().optional(),

  entityId: z.string().uuid().nullable().optional(),
  // Vendor lane: first-class link to a Supplier (vendor company).
  supplierId: z.string().uuid().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  isPrimary: z.boolean().default(false),
  // T2/T3 — record lifecycle (orthogonal to the funnel lifecycleStage).
  // Server-managed: 'merged' set by the merge engine (T3); mergedIntoId/mergedAt
  // let a tombstone render its "Merged into X" redirect banner (U2).
  recordState: RecordStateSchema.optional(),
  mergedIntoId: z.string().uuid().nullable().optional(),
  mergedAt: z.date().nullable().optional(),
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  // Relationship data
  entity: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      logoUrl: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  // Embedded relationship objects are summaries on list rows and complete on
  // the detail read; see the *SummarySchema definitions.
  entityLinks: z.array(ContactEntityLinkSummarySchema).optional(),
  supplier: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  relationshipTypeAssignments: z
    .array(ContactRelationshipTypeAssignmentSummarySchema)
    .optional(),
  relationshipTypes: z.array(ContactRelationshipTypeSummarySchema).optional(),
  contactTypes: z.array(ContactRelationshipTypeSummarySchema).optional(),
  primaryRelationshipType:
    ContactRelationshipTypeAssignmentSummarySchema.nullable().optional(),
  creator: z
    .object({
      id: z.string().uuid(),
      email: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      displayName: z.string().nullable().optional(),
      image: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  assignedTo: z
    .object({
      id: z.string().uuid(),
      email: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      displayName: z.string().nullable().optional(),
      image: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  customFields: z.record(z.string(), z.any()).optional(),
})

export type Contact = z.infer<typeof ContactSchema>

// Create contact schema
export const CreateContactSchema = ContactSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  createdBy: true,
  recordState: true, // Server-managed lifecycle (T2/T3) — never client-set
  mergedIntoId: true,
  mergedAt: true,
  captureId: true, // Server-managed provenance (T1) — never client-set
  capture: true,
  entity: true,
  entityLinks: true,
  supplier: true,
  relationshipTypeAssignments: true,
  relationshipTypes: true,
  contactTypes: true,
  primaryRelationshipType: true,
  creator: true,
}).extend({
  customFields: z.record(z.string(), z.any()).optional(),
  relationshipTypeAssignments: z
    .array(AssignContactRelationshipTypeSchema)
    .optional(),
})

export type CreateContact = z.infer<typeof CreateContactSchema>

// Update contact schema
export const UpdateContactSchema = CreateContactSchema.partial().extend({
  customFields: z.record(z.string(), z.any()).optional(),
})

export type UpdateContact = z.infer<typeof UpdateContactSchema>

// Contact response schema
export const ContactResponseSchema = z.object({
  data: ContactSchema,
  message: z.string().optional(),
})

export type ContactResponse = z.infer<typeof ContactResponseSchema>

// Contacts response schema
export const ContactsResponseSchema = z.object({
  data: z.array(ContactSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type ContactsResponse = z.infer<typeof ContactsResponseSchema>

// =====================
// ContactRelationship schemas
// =====================
export const ContactRelationshipSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  fromContactId: z.string().uuid(),
  toContactId: z.string().uuid(),
  type: z.string(), // ContactRelationshipTypeEnum at the service layer; string here to allow custom labels
  label: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Optional joined data
  fromContact: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().nullable().optional(),
      imageKey: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  toContact: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().nullable().optional(),
      imageKey: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

export type ContactRelationship = z.infer<typeof ContactRelationshipSchema>

export const CreateContactRelationshipSchema = z.object({
  fromContactId: z.string().uuid(),
  toContactId: z.string().uuid(),
  type: z.string().min(1),
  label: z.string().max(100).nullable().optional(),
  notes: z.string().nullable().optional(),
})

export type CreateContactRelationship = z.infer<
  typeof CreateContactRelationshipSchema
>

export const UpdateContactRelationshipSchema =
  CreateContactRelationshipSchema.partial()

export type UpdateContactRelationship = z.infer<
  typeof UpdateContactRelationshipSchema
>

export const ContactRelationshipResponseSchema = z.object({
  data: ContactRelationshipSchema,
  message: z.string().optional(),
})

export type ContactRelationshipResponse = z.infer<
  typeof ContactRelationshipResponseSchema
>

export const ContactRelationshipsResponseSchema = z.object({
  data: z.array(ContactRelationshipSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type ContactRelationshipsResponse = z.infer<
  typeof ContactRelationshipsResponseSchema
>
